/**
 * Shared color conventions for the Professional Options Dashboard.
 *
 * Mirrors `StockDataPage.tsx`'s `SIGNAL_COLORS` 5-way green/red/grey scale
 * (STRONG_BULLISH -> STRONG_BEARISH) so this page looks consistent with the
 * rest of the app, extended here to also cover the dashboard's own
 * regime/strength vocabulary (which uses different string values but the
 * same "green = bullish, red = bearish, grey = neutral" idea).
 */
import type { ClassicRegime, MarketBias, RegimeStrength, TrendDirection } from "@/types";

export const MARKET_BIAS_COLORS: Record<MarketBias, string> = {
  STRONG_BULLISH: "#1B5E20",
  BULLISH: "#81C784",
  NEUTRAL: "#9E9E9E",
  BEARISH: "#FB8C00",
  STRONG_BEARISH: "#C62828",
};

export const MARKET_BIAS_LABELS: Record<MarketBias, string> = {
  STRONG_BULLISH: "Strong Bullish",
  BULLISH: "Bullish",
  NEUTRAL: "Neutral",
  BEARISH: "Bearish",
  STRONG_BEARISH: "Strong Bearish",
};

/** Fallback for any bias string the backend sends that isn't one of the 5
 * documented values — grey, never a guessed color. */
export function marketBiasColor(bias?: string | null): string {
  if (bias && bias in MARKET_BIAS_COLORS) return MARKET_BIAS_COLORS[bias as MarketBias];
  return MARKET_BIAS_COLORS.NEUTRAL;
}

export function marketBiasLabel(bias?: string | null): string {
  if (!bias) return "—";
  if (bias in MARKET_BIAS_LABELS) return MARKET_BIAS_LABELS[bias as MarketBias];
  return bias;
}

// Classic OI regimes (LONG_BUILDUP etc.) aren't a bullish/bearish scale in
// the same sense, but a trader still expects buildups to read "warmer" and
// unwinding/covering to read "cooler" — kept clearly distinct from the
// 5-way bias palette above (different hues) to avoid implying they're the
// same scale.
export const REGIME_COLORS: Record<ClassicRegime, string> = {
  LONG_BUILDUP: "#2E7D32",
  SHORT_BUILDUP: "#C62828",
  LONG_UNWINDING: "#F57F17",
  SHORT_COVERING: "#1565C0",
  NEUTRAL: "#9E9E9E",
};

export const REGIME_LABELS: Record<ClassicRegime, string> = {
  LONG_BUILDUP: "Long Buildup",
  SHORT_BUILDUP: "Short Buildup",
  LONG_UNWINDING: "Long Unwinding",
  SHORT_COVERING: "Short Covering",
  NEUTRAL: "Neutral",
};

export function regimeColor(regime?: string | null): string {
  if (regime && regime in REGIME_COLORS) return REGIME_COLORS[regime as ClassicRegime];
  return REGIME_COLORS.NEUTRAL;
}

export function regimeLabel(regime?: string | null): string {
  if (!regime) return "—";
  if (regime in REGIME_LABELS) return REGIME_LABELS[regime as ClassicRegime];
  return regime;
}

export const REGIME_STRENGTH_COLORS: Record<RegimeStrength, string> = {
  WEAK: "#9E9E9E",
  MODERATE: "#FB8C00",
  STRONG: "#1B5E20",
};

export function regimeStrengthColor(strength?: string | null): string {
  if (strength && strength in REGIME_STRENGTH_COLORS) return REGIME_STRENGTH_COLORS[strength as RegimeStrength];
  return REGIME_STRENGTH_COLORS.WEAK;
}

export function trendArrow(direction?: TrendDirection | null): string {
  if (direction === "up") return "▲";
  if (direction === "down") return "▼";
  if (direction === "flat") return "▬";
  return "";
}

export function trendColor(direction?: TrendDirection | null): "success.main" | "error.main" | "text.secondary" {
  if (direction === "up") return "success.main";
  if (direction === "down") return "error.main";
  return "text.secondary";
}

/** Formats a nullable Decimal-as-string field, `"—"` for null/undefined/NaN
 * — never renders a guessed 0. */
export function fmtNum(value?: string | null, decimals = 2): string {
  if (value === null || value === undefined) return "—";
  const n = Number.parseFloat(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function fmtInt(value?: number | null): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("en-IN");
}

/**
 * Generic semantic roles for the 2026-08 UI redesign (spec: "Green=Bullish,
 * Red=Bearish, Orange=Warning, Grey=Neutral, Blue=Information — avoid
 * inconsistent colour usage"). These reuse the exact hex values already
 * defined above (STRONG_BULLISH/STRONG_BEARISH/BEARISH/NEUTRAL) rather than
 * introducing new ones, so anything drawing on `SEMANTIC_COLORS` stays
 * visually identical to the existing bias/regime chips. Only "info" (blue)
 * is genuinely new — no blue token existed anywhere in this file before.
 */
export const SEMANTIC_COLORS = {
  bullish: MARKET_BIAS_COLORS.STRONG_BULLISH,
  bearish: MARKET_BIAS_COLORS.STRONG_BEARISH,
  warning: MARKET_BIAS_COLORS.BEARISH,
  neutral: MARKET_BIAS_COLORS.NEUTRAL,
  info: "#1565C0",
} as const;
